import React, { useEffect, useState } from 'react';
import { useRoute, useLocation, Link } from 'wouter';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { doc, getDoc, collection, query, where, orderBy, onSnapshot, limit } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { 
  Plus, 
  Minus, 
  ArrowLeft, 
  Search, 
  Share2, 
  Heart,
  Clock
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import toast from 'react-hot-toast';

export default function ProductDetail() {
  const [, params] = useRoute<{ id: string }>('/product/:id');
  const [, setLocation] = useLocation();
  const productId = params?.id;
  const { items, addToCart, updateQuantity, removeFromCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  
  const [product, setProduct] = useState<any>(null);
  const [relatedProducts, setRelatedProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeight, setSelectedWeight] = useState('');
  const [customWeight, setCustomWeight] = useState<number | ''>('');
  const [customUnit, setCustomUnit] = useState<'g' | 'kg'>('g');
  const [isCustomMode, setIsCustomMode] = useState(false);
  
  const cartItem = items.find(item => 
    item.id === productId && 
    item.selectedWeight === (isCustomMode ? `${customWeight}${customUnit} (Custom)` : selectedWeight)
  );
  const quantity = cartItem ? cartItem.quantity : 0;
  const isLiked = productId ? isInWishlist(productId) : false;

  useEffect(() => {
    if (!productId) return;

    window.scrollTo(0, 0);
    setLoading(true);

    const fetchProduct = async () => {
      try {
        const docRef = doc(db, 'products', productId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setProduct({ id: docSnap.id, ...data });
          setSelectedWeight(data.variants?.[0]?.weight || '');

          // Fetch related products
          const qRelated = query(
            collection(db, 'products'),
            where('category', '==', data.category),
            limit(6)
          );
          const relatedSnap = onSnapshot(qRelated, (snapshot) => {
            setRelatedProducts(snapshot.docs
              .map(doc => ({ id: doc.id, ...doc.data() }))
              .filter(p => p.id !== productId)
            );
          });
          return () => relatedSnap();
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  const handleAdd = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!product) return;

    if (isCustomMode) {
      if (!customWeight || Number(customWeight) <= 0) {
        toast.error('Please enter a valid weight');
        return;
      }
      const weightInGrams = customUnit === 'kg' ? Number(customWeight) * 1000 : Number(customWeight);
      const calculatedPrice = Math.round(weightInGrams * (product.pricePerGram || 0));
      const weightString = `${customWeight}${customUnit} (Custom)`;
      
      if (cartItem) {
        updateQuantity(cartItem.cartItemId, quantity + 1);
      } else {
        addToCart(product, weightString, 1, calculatedPrice);
        toast.success('Added custom quantity to cart');
      }
      return;
    }

    if (!selectedWeight) {
      console.error('Missing weight', { selectedWeight });
      return;
    }

    if (cartItem) {
      updateQuantity(cartItem.cartItemId, quantity + 1);
    } else {
      addToCart(product, selectedWeight, 1);
      toast.success('Added to cart');
    }
  };

  const handleMinus = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (quantity > 1) {
      updateQuantity(cartItem!.cartItemId, quantity - 1);
    } else if (quantity === 1) {
      removeFromCart(cartItem!.cartItemId);
    }
  };

  const handleToggleLike = () => {
    if (productId) {
      toggleWishlist(productId);
      if (!isLiked) {
        toast.success('Added to favorites');
      } else {
        toast('Removed from favorites');
      }
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: product?.name || 'Artisanal Spices',
      text: product?.description || 'Check out this amazing spice!',
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard!');
      }
    } catch (err) {
      // Don't log if the user cancelled
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('Error sharing:', err);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
    </div>
  );

  if (!product) return (
    <div className="min-h-screen bg-[#111] flex items-center justify-center p-10">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-white mb-4">Product Not Found</h2>
        <button onClick={() => setLocation('/products')} className="text-spice-red font-bold underline">Back to Shop</button>
      </div>
    </div>
  );

  const currentVariant = product.variants?.find((v: any) => v.weight === selectedWeight);
  
  // Decide which image to show
  let mainImage = product.image;
  if (isCustomMode && product.customImage) {
    mainImage = product.customImage;
  } else if (!isCustomMode && currentVariant?.image) {
    mainImage = currentVariant.image;
  }

  let price = 0;
  let displayWeight = selectedWeight;

  if (isCustomMode) {
    const weightInGrams = customUnit === 'kg' ? Number(customWeight || 0) * 1000 : Number(customWeight || 0);
    price = Math.round(weightInGrams * (product.pricePerGram || 0));
    displayWeight = customWeight ? `${customWeight}${customUnit}` : `0${customUnit}`;
  } else {
    price = currentVariant ? currentVariant.price : product.price;
  }

  const isOutOfStock = product?.isOutOfStock === true || product?.stock === 0;
  const mrp = Math.round(price * 1.25); // Simulation of MRP if not present

  return (
    <div className="min-h-screen bg-[#FBF9F4] text-[#1B120C] pb-36">
      {/* Top Navigation Overlay */}
      <div className="fixed top-0 left-0 right-0 z-40 flex justify-between items-center px-3 sm:px-4 py-3 bg-gradient-to-b from-black/70 via-black/40 to-transparent">
        <button 
          onClick={() => window.history.back()}
          aria-label="Go back"
          className="min-w-[44px] min-h-[44px] w-11 h-11 bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/20 active:scale-90 transition-all cursor-pointer flex-shrink-0"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        
        {/* Dynamic product name display in header */}
        <div className="flex-1 mx-2 overflow-hidden">
          <p className="text-[10px] font-black uppercase tracking-[0.1em] text-white truncate text-center bg-black/50 backdrop-blur-md px-3 py-2 rounded-full border border-white/20 shadow-lg">
            {product?.name}
          </p>
        </div>

        <div className="flex gap-1.5 flex-shrink-0">
          <button 
            onClick={handleToggleLike}
            aria-label="Add to wishlist"
            className={`min-w-[44px] min-h-[44px] w-11 h-11 bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 active:scale-90 cursor-pointer transition-colors ${isLiked ? 'text-spice-accent border-spice-accent/40' : 'text-white'}`}
          >
            <Heart className={`w-5 h-5 ${isLiked ? 'fill-spice-accent' : ''}`} />
          </button>
          <button 
            onClick={() => setLocation('/products')}
            aria-label="Browse spices"
            className="min-w-[44px] min-h-[44px] w-11 h-11 bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/20 active:scale-90 cursor-pointer"
          >
            <Search className="w-5 h-5" />
          </button>
          <button 
            onClick={handleShare}
            aria-label="Share product"
            className="min-w-[44px] min-h-[44px] w-11 h-11 bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/20 active:scale-90 cursor-pointer"
          >
            <Share2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Image Header */}
      <div className="relative w-full aspect-[4/5] md:aspect-video lg:aspect-[21/9] overflow-hidden bg-black/20">
        <AnimatePresence mode="wait">
          <motion.img 
            key={mainImage}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            src={mainImage} 
            alt={product.name} 
            referrerPolicy="no-referrer"
            className={`w-full h-full object-cover ${isOutOfStock ? 'blur-[8px] opacity-40 scale-102' : ''}`}
          />
        </AnimatePresence>
        
        {isOutOfStock && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/40 gap-3">
             <div className="px-6 py-3 bg-red-600/90 border border-red-500/20 backdrop-blur-xl text-white font-black text-sm uppercase tracking-[0.25em] rounded-2xl shadow-2xl">
               OUT OF STOCK
             </div>
             <p className="text-[10px] text-white/60 font-black uppercase tracking-widest bg-black/40 px-3 py-1.5 rounded-full border border-white/5">Temporarily Unavailable</p>
          </div>
        )}

        {/* Pagination Dots Simulator */}
        <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-1.5 z-10">
          <div className="w-2 h-2 rounded-full bg-white"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-white/30"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-white/30"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-white/30"></div>
        </div>
      </div>

      {/* Product Content Block */}
      <div className="px-5 mt-6 space-y-6">
        {/* Title and Weight */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-black leading-tight mb-1 text-white uppercase tracking-tight">{product.name}</h1>
          <p className="text-xs sm:text-base text-white/50 font-bold uppercase tracking-wider">{displayWeight || '1 unit'} {product.variants?.length > 1 && !isCustomMode ? `(${product.variants.length} options)` : ''}</p>
        </div>

        {/* Pricing */}
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-white">Rs. {price.toFixed(2)}</span>
          <span className="text-sm text-white/60 line-through font-medium">MRP Rs. {mrp.toFixed(2)}</span>
          {isCustomMode && product.pricePerGram && (
            <span className="text-[10px] text-spice-accent font-bold uppercase ml-2 bg-spice-accent/10 px-2 py-1 rounded">
              Rs. {product.pricePerGram.toFixed(2)}/gram
            </span>
          )}
        </div>



        {/* Mode selection if custom weight is supported */}
        {product.pricePerGram > 0 && (
          <div className="pt-2">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[11px] text-white uppercase font-black tracking-[0.2em]">Select Mode</p>
              <div className="flex bg-[#1C1F26] rounded-xl p-1.5 border border-white/10 shadow-2xl">
                <button 
                  onClick={() => setIsCustomMode(false)}
                  className={`px-8 py-3 text-[11px] font-black uppercase rounded-lg transition-all duration-300 ${!isCustomMode ? 'bg-spice-accent text-white shadow-lg shadow-spice-accent/40 scale-105' : 'text-white hover:text-white/70'}`}
                >
                  Standard
                </button>
                <button 
                  onClick={() => setIsCustomMode(true)}
                  className={`px-8 py-3 text-[11px] font-black uppercase rounded-lg transition-all duration-300 ${isCustomMode ? 'bg-spice-accent text-white shadow-lg shadow-spice-accent/40 scale-105' : 'text-white hover:text-white/70'}`}
                >
                  Custom
                </button>
              </div>
            </div>

            {isCustomMode ? (
              <div className="space-y-4">
                <div className="bg-[#1C1F26] p-4 rounded-2xl border border-spice-accent/20 flex items-center gap-4">
                  <div className="flex-1">
                    <p className="text-[9px] text-white uppercase font-bold tracking-widest mb-2">Enter Weight</p>
                    <input 
                      type="number" 
                      value={customWeight}
                      onChange={(e) => setCustomWeight(e.target.value === '' ? '' : Number(e.target.value))}
                      placeholder="e.g. 500"
                      className="bg-transparent text-xl font-bold w-full focus:outline-none placeholder:text-white/40"
                    />
                  </div>
                  <div className="flex bg-black/40 rounded-xl p-1 gap-1">
                    {['g', 'kg'].map((u) => (
                      <button
                        key={u}
                        onClick={() => setCustomUnit(u as 'g' | 'kg')}
                        className={`w-10 h-10 rounded-lg text-xs font-bold uppercase transition-all ${customUnit === u ? 'bg-spice-accent text-white' : 'text-white'}`}
                      >
                        {u}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-[10px] text-white/60 italic">Note: Billing will be done based on the exact rate per gram (₹{product.pricePerGram}/g).</p>
              </div>
            ) : null}
          </div>
        )}

        {/* Variant Selection */}
        {product.variants?.length > 1 && !isCustomMode && (
          <div className="pt-2">
            <p className="text-[10px] text-white uppercase font-bold tracking-widest mb-3">Select Variant</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 relative z-10">
              {product.variants.map((v: any) => (
                <button
                  key={v.weight}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedWeight(v.weight);
                  }}
                  className={`p-3 rounded-xl border flex flex-col items-center gap-1 transition-all cursor-pointer ${
                    selectedWeight === v.weight 
                      ? 'bg-white text-black border-white shadow-lg scale-[1.02]' 
                      : 'bg-[#1C1F26] text-white border-white/5 hover:border-white/20'
                  }`}
                >
                  <span className="text-[10px] font-black uppercase tracking-tight">{v.weight}</span>
                  <span className={`text-[11px] font-bold ${selectedWeight === v.weight ? 'text-black/60' : 'text-white/60'}`}>₹{v.price}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Description Section */}
        <div className="pt-6 border-t border-white/5">
          <h3 className="text-sm font-bold uppercase tracking-widest mb-4">About this product</h3>
          <p className="text-sm text-white leading-relaxed italic">
            {product.description}
          </p>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="pt-8">
            <div className="flex justify-between items-end mb-6">
               <h3 className="text-sm font-bold uppercase tracking-widest">Top products in this category</h3>
               <Link href="/products" className="text-[10px] font-bold text-spice-accent uppercase tracking-widest">View all</Link>
            </div>
            <div className="flex gap-4 overflow-x-auto no-scrollbar pb-4 -mx-5 px-5">
              {relatedProducts.map(rp => (
                <div 
                  key={rp.id} 
                  className="min-w-[140px] w-[140px] bg-[#1C1F26] rounded-2xl border border-white/5 p-3 flex flex-col gap-2 cursor-pointer related-product-card"
                  onClick={() => setLocation(`/product/${rp.id}`)}
                >
                  <div className="aspect-square rounded-xl overflow-hidden mb-2 bg-[#252A34] relative">
                    <img src={rp.image} alt={rp.name} className="w-full h-full object-cover" />
                    <button className="absolute top-2 right-2 p-1.5 bg-black/40 backdrop-blur-md rounded-full cursor-pointer">
                       <Heart className="w-3 h-3 text-white/80" />
                    </button>
                  </div>
                  <h4 className="text-[11px] font-bold truncate">{rp.name}</h4>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold tracking-tight">₹{rp.price}</span>
                    <button className="w-7 h-7 bg-spice-accent rounded-lg flex items-center justify-center cursor-pointer">
                       <Plus className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#FAF5EB]/95 backdrop-blur-xl border-t border-[rgba(27,18,12,0.12)] p-3 sm:p-5 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] shadow-xl">
        <div className="max-w-md mx-auto flex items-center justify-between gap-4 sm:gap-6">
          <div className="flex flex-col">
            <p className="text-xl sm:text-2xl font-black text-[#1B120C]">Rs. {(price * (quantity || 1)).toFixed(2)}</p>
            <p className="text-[10px] text-[#63574E] font-black uppercase tracking-wider">{displayWeight || '1 unit'}</p>
            <p className="text-[8px] text-[#D97736] font-bold uppercase">Inclusive of all taxes</p>
          </div>
          
          <AnimatePresence mode="wait">
            {isOutOfStock ? (
              <button
                disabled
                className="flex-1 max-w-[200px] h-12 bg-stone-200 text-stone-500 rounded-xl font-black uppercase tracking-wider flex items-center justify-center border border-stone-300 cursor-not-allowed text-xs"
              >
                Sold Out
              </button>
            ) : quantity === 0 ? (
              <motion.button
                key="add-btn"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                onClick={(e) => handleAdd(e)}
                className="flex-1 max-w-[200px] h-12 bg-spice-accent text-white rounded-xl font-black uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg shadow-spice-accent/25 active:scale-95 transition-all text-xs cursor-pointer"
              >
                Add to cart
              </motion.button>
            ) : (
              <motion.div
                key="qty-ctrl"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex-1 max-w-[200px] h-12 bg-[#1B120C] text-white rounded-xl overflow-hidden flex relative z-10 shadow-md"
              >
                <button 
                  type="button"
                  onClick={(e) => handleMinus(e)}
                  className="flex-1 flex items-center justify-center hover:bg-white/10 active:scale-90 transition-all border-r border-white/10 cursor-pointer"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <div className="flex-[0.8] flex items-center justify-center font-black text-base text-white">
                  {quantity}
                </div>
                <button 
                  type="button"
                  onClick={(e) => handleAdd(e)}
                  className="flex-1 flex items-center justify-center hover:bg-white/10 active:scale-90 transition-all border-l border-white/10 cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
